<template>
  <TransitionRoot :show="modelValue" as="template">
    <Dialog as="div" :class="['fixed inset-0', zClass]" @close="handleClose">
      <!-- Backdrop -->
      <TransitionChild
        as="template"
        enter="transition-opacity ease-out duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity ease-in duration-150"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-theme-bg/40 backdrop-blur-sm" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div
          class="absolute inset-y-0 flex max-w-full"
          :class="side === 'left' ? 'left-0' : 'right-0'"
        >
          <TransitionChild
            as="template"
            enter="transform transition ease-out duration-250"
            :enter-from="side === 'left' ? '-translate-x-full' : 'translate-x-full'"
            enter-to="translate-x-0"
            leave="transform transition ease-in duration-200"
            leave-from="translate-x-0"
            :leave-to="side === 'left' ? '-translate-x-full' : 'translate-x-full'"
          >
            <DialogPanel
              :class="[
                'flex h-full w-screen flex-col overflow-hidden bg-theme-surface text-theme-text shadow-theme-dialog dark:shadow-theme-shadow/60',
                side === 'left' ? 'border-r' : 'border-l',
                'border-theme-border',
                sizeClass,
                panelClass,
              ]"
            >
              <header
                v-if="title || $slots.header || showClose"
                class="flex items-center justify-between gap-4 border-b border-theme-border px-5 py-4"
              >
                <slot name="header">
                  <DialogTitle
                    v-if="title"
                    as="h2"
                    class="text-lg font-semibold leading-6 text-theme-text"
                  >
                    {{ title }}
                  </DialogTitle>
                </slot>
                <button
                  v-if="showClose"
                  type="button"
                  class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-theme-sm text-theme-text-secondary transition hover:bg-theme-bg-subtle hover:text-theme-text"
                  :aria-label="closeLabel || 'Close'"
                  @click="requestClose"
                >
                  <icon :path="mdiClose" size="18" />
                </button>
              </header>

              <div class="min-h-0 flex-1 overflow-y-auto" :class="bodyClass">
                <slot />
              </div>

              <footer
                v-if="$slots.footer"
                class="border-t border-theme-border px-5 py-4"
              >
                <slot name="footer" />
              </footer>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
  /**
   * AppDrawer — slide-in side panel on the shared HeadlessUI Dialog shell.
   *
   * The drawer counterpart to AppModal/AppDialog: same themed chrome, focus
   * trap, scroll-lock, Escape and portaling (Headless owns those), but the
   * panel is anchored to an edge and slides in. Use for contextual editors and
   * settings that shouldn't take over the whole screen (e.g. dashboard panel
   * settings). Sits at `z-drawer` — below modals (`z-dialog`) by default.
   */
  import Icon from '@/components/Icon';
  import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
  import { mdiClose } from '@mdi/js';
  import { computed } from 'vue';

  defineOptions({ name: 'AppDrawer' });

  const props = defineProps({
    /** v-model — controls visibility. */
    modelValue: { type: Boolean, default: false },
    /** Edge the drawer is anchored to. */
    side: { type: String, default: 'right', validator: (v) => ['left', 'right'].includes(v) },
    /** Width preset. */
    size: { type: String, default: 'md', validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v) },
    /** Optional header title. */
    title: { type: String, default: '' },
    /** Show the header close (X) button. */
    showClose: { type: Boolean, default: true },
    /** Accessible label for the close button. */
    closeLabel: { type: String, default: '' },
    /** When true (default), Escape / backdrop-click close the drawer. */
    dismissible: { type: Boolean, default: true },
    /** Extra class on the scrollable body wrapper. */
    bodyClass: { type: [String, Array, Object], default: 'px-5 py-4' },
    /** Extra classes merged onto the panel. */
    panelClass: { type: [String, Array, Object], default: '' },
    /** Stacking context. Drawers sit below modals by default. */
    zClass: { type: String, default: 'z-drawer' },
  });

  const emit = defineEmits(['update:modelValue', 'close']);

  const SIZE_CLASSES = {
    sm: 'max-w-xs',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
  };
  const sizeClass = computed(() => SIZE_CLASSES[props.size] || SIZE_CLASSES.md);

  // The header X always closes. Headless `@close` (Esc / backdrop) only closes
  // when `dismissible`.
  function requestClose() {
    emit('close');
    emit('update:modelValue', false);
  }

  function handleClose() {
    emit('close');
    if (props.dismissible) emit('update:modelValue', false);
  }
</script>
